// src/components/CustomerAttachments.js
import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { Dialog } from "primereact/dialog";
import axiosInstance from "../../services/axiosInstance";
import { handleApiError, handleApiSuccess } from "../apiErrorHandler";

const MAX_SIDE = 1600;            // px
const TARGET_MAX_KB = 400;        // ~400KB
const QUALITY_START = 0.8;        // 0..1

async function compressImage(file) {
  // uses canvas to resize + compress to JPEG/WebP (browser decides)
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);

  let quality = QUALITY_START;
  let blob = await new Promise(res => canvas.toBlob(res, "image/webp", quality)) || file;

  for (let i = 0; i < 4; i++) {
    if (!blob) break;
    if (blob.size / 1024 <= TARGET_MAX_KB || quality <= 0.55) break;
    quality -= 0.05;
    blob = await new Promise(res => canvas.toBlob(res, "image/webp", quality));
  }

  // Fallback to jpeg if webp returns null
  if (!blob) {
    blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.8));
  }
  const out = new File([blob], (file.name || "capture") + (blob.type === "image/webp" ? ".webp" : ".jpg"), { type: blob.type });
  return out;
}

export default function CustomerAttachments({ customerId }) {
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const fetchItems = async () => {
    try {
      const { data } = await axiosInstance.get(`/api/customer-attachments/?customer=${customerId}`);
      setItems(data);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => { if (customerId) fetchItems(); }, [customerId]);

  // ----- File picker (mobile-friendly + capture attr) -----
  const inputRef = useRef(null);
  const onPick = () => inputRef.current?.click();
  const onInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const small = await compressImage(file);
    await uploadFile(small, note);
    e.target.value = "";
  };

  // ----- Webcam capture -----
  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      streamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 20);
    } catch (err) {
      handleApiError(err);
    }
  };
  const closeCamera = () => {
    const s = streamRef.current;
    if (s) { s.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setCameraOpen(false);
  };
  const captureAndUpload = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    closeCamera();
    let blob = await new Promise(res => canvas.toBlob(res, "image/webp", 0.8));
    if (!blob) blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.8));
    const file = new File([blob], "capture.jpg", { type: blob.type || "image/jpeg" });
    const small = await compressImage(file);
    await uploadFile(small, note);
  };

  // ----- Upload -----
  const uploadFile = async (file, noteVal) => {
    const form = new FormData();
    form.append("customer", customerId);
    form.append("note", noteVal || "");
    form.append("file", file);

    try {
      await axiosInstance.post("/api/customer-attachments/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNote("");
      handleApiSuccess("Attachment uploaded");
      fetchItems();
    } catch (err) {
      handleApiError(err);
    }
  };

  const remove = async (id) => {
    try {
      await axiosInstance.delete(`/api/customer-attachments/${id}/`);
      handleApiSuccess("Attachment deleted");
      setItems(items.filter(x => x.id !== id));
    } catch (err) {
      handleApiError(err);
    }
  };

  function normalizeToHttps(url) {
    if (!url) return url;
    try {
      const u = new URL(url, window.location.origin);
      if (u.protocol === 'http:') u.protocol = 'https:';
      return u.toString();
    } catch (e) {
      return url.replace(/^http:\/\//i, 'https://');
    }
  }
  return (
    <div>
      <div className="flex gap-2">
        <div className="flex">
          <label className="block text-sm font-medium mb-1">Note: </label>
          <InputTextarea value={note} onChange={(e) => setNote(e.target.value)} autoResize />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"   // mobile: opens camera directly
          style={{ display: "none" }}
          onChange={onInputChange}
        />
        <Button label="Upload Photo" icon="pi pi-upload" onClick={onPick} className="p-button-info" style={{borderRadius:"6px", fontSize: "14px"}} size="medium"/>
        <Button label="Use Camera" icon="pi pi-camera" onClick={openCamera} className="p-button-secondary" style={{borderRadius:"6px", fontSize: "14px"}} size="medium"/>
      </div></div>
    <div><p></p></div>
    <div className="flex flex-col gap-3">
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, 140px)", gap: 12 }}>
        {items.map(att => (
          <div key={att.id} className="border rounded p-2 flex flex-col items-center gap-2">
            <a href={att.file} target="_blank" rel="noreferrer">
              <img src={att.file} alt="" style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8 }} />
            </a>
            <div className="text-xs line-clamp-2" title={att.original_filename || ""}>
              {att.note || att.original_filename || "Photo"}
            </div>
            <div className="flex gap-2">
              <a className="p-button p-button-text p-1" href={normalizeToHttps(att.file)} download>
                <i className="pi pi-download" />
              </a>
              <Button icon="pi pi-trash" text rounded severity="danger" onClick={() => remove(att.id)} />
            </div>
          </div>
        ))}
      </div>
  
      <Dialog header="Camera" visible={cameraOpen} onHide={closeCamera} style={{ width: 420 }}>
        <video ref={videoRef} style={{ width: "100%", borderRadius: 8 }} />
        <div className="flex justify-end gap-2 mt-3">
          <Button label="Cancel" onClick={closeCamera} />
          <Button label="Capture & Upload" icon="pi pi-check" onClick={captureAndUpload} />
        </div>
      </Dialog>
    </div></div>
  );
}
